// BEGIN FILE: src/hooks/useKarmicFriction.ts
// Loads / saves planet friction responses from Supabase.
// Exposes:
//   • frictionScoresByPlanet — latest round 0-100 per planet
//   • planetDeltas           — score change vs previous round
//   • latestRoundId          — used by KarmicPatternDetection for resume
//   • latestCompletedAt      — date of last complete survey
//   • saveResponse           — upserts a single question answer
//   • loadLatestScores       — refreshes data (call after survey completes)

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../utils/supabaseClient';
import type { PlanetName } from '../astro/karmic/frictionQuestions';
import { PLANET_ORDER, FRICTION_QUESTIONS } from '../astro/karmic/frictionQuestions';
import { getFrictionLevel, getPlanetNarrative } from '../astro/karmic/karmicFrictionEngine';

export interface PlanetFrictionResult {
  planet: PlanetName;
  score: number;                             // 0-100
  level: 'Low' | 'Moderate' | 'High';
  narrative: string;
  delta: number | null;                      // vs previous round (negative = improved)
  deltaLabel: 'Improved' | 'Unchanged' | 'Worsening' | null;
  lastTakenAt: string | null;
}

export interface UseKarmicFrictionReturn {
  frictionScoresByPlanet: Record<string, number>;  // only planets with data
  planetResults: PlanetFrictionResult[];
  latestRoundId: string | null;
  latestCompletedAt: string | null;
  isLoading: boolean;
  saveResponse: (
    roundId: string,
    questionId: string,
    planet: string,
    score: number,
    birthProfileId?: string | null,
  ) => Promise<void>;
  loadLatestScores: () => Promise<void>;
}

const QUESTIONS_PER_PLANET = 3; // 3 randomly selected per planet per round (bank has 5 each)

export function useKarmicFriction(
  userId: string | null | undefined,
  birthProfileId?: string | null,
): UseKarmicFrictionReturn {
  const [frictionScoresByPlanet, setFrictionScoresByPlanet] = useState<Record<string, number>>({});
  const [planetResults, setPlanetResults] = useState<PlanetFrictionResult[]>([]);
  const [latestRoundId, setLatestRoundId] = useState<string | null>(null);
  const [latestCompletedAt, setLatestCompletedAt] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const loadLatestScores = useCallback(async () => {
    if (!userId) {
      setFrictionScoresByPlanet({});
      setPlanetResults([]);
      setLatestRoundId(null);
      setLatestCompletedAt(null);
      return;
    }

    setIsLoading(true);
    try {
      // Query the aggregate view: per-round per-planet scores
      // We need the 2 most recent complete rounds (for delta calculation)
      // IMPORTANT: filter by birth_profile_id so survey results are profile-specific.
      // Without this filter, all family profiles would share the same survey data.
      let scoreQuery = supabase
        .from('planet_friction_scores')
        .select('round_id, planet, friction_score_pct, completed_at, questions_answered')
        .eq('user_id', userId)
        .gte('questions_answered', QUESTIONS_PER_PLANET - 1)  // consider "complete enough"
        .order('completed_at', { ascending: false });

      if (birthProfileId) {
        scoreQuery = scoreQuery.eq('birth_profile_id', birthProfileId);
      } else {
        scoreQuery = scoreQuery.is('birth_profile_id', null);
      }

      const { data, error } = await scoreQuery;

      if (error) {
        console.error('[useKarmicFriction] load error:', error);
        return;
      }

      if (!data || data.length === 0) {
        setFrictionScoresByPlanet({});
        setPlanetResults([]);
        setLatestRoundId(null);
        setLatestCompletedAt(null);
        return;
      }

      // Group rows by round_id, ordered by completed_at desc
      const roundsMap = new Map<string, { completedAt: string; scores: Record<string, number> }>();
      data.forEach((row: any) => {
        const existing = roundsMap.get(row.round_id);
        if (!existing) {
          roundsMap.set(row.round_id, {
            completedAt: row.completed_at,
            scores: { [row.planet]: Number(row.friction_score_pct) },
          });
        } else {
          existing.scores[row.planet] = Number(row.friction_score_pct);
          // Keep latest completedAt for this round
          if (row.completed_at > existing.completedAt) {
            existing.completedAt = row.completed_at;
          }
        }
      });

      // Sort rounds by completedAt desc
      const sortedRounds = Array.from(roundsMap.entries()).sort(
        ([, a], [, b]) => b.completedAt.localeCompare(a.completedAt),
      );

      if (sortedRounds.length === 0) return;

      const [latestRoundEntry, previousRoundEntry] = sortedRounds;
      const [latestId, latestData] = latestRoundEntry;
      const prevScores = previousRoundEntry?.[1]?.scores ?? {};

      setLatestRoundId(latestId);
      setLatestCompletedAt(latestData.completedAt);
      setFrictionScoresByPlanet(latestData.scores);

      // Build PlanetFrictionResult array for the results screen
      const results: PlanetFrictionResult[] = PLANET_ORDER
        .filter(planet => latestData.scores[planet] !== undefined)
        .map(planet => {
          const score = latestData.scores[planet];
          const prevScore = prevScores[planet] ?? null;
          const delta = prevScore !== null ? score - prevScore : null;
          let deltaLabel: PlanetFrictionResult['deltaLabel'] = null;
          if (delta !== null) {
            if (delta < -5) deltaLabel = 'Improved';
            else if (delta > 5) deltaLabel = 'Worsening';
            else deltaLabel = 'Unchanged';
          }
          return {
            planet,
            score,
            level: getFrictionLevel(score),
            narrative: getPlanetNarrative(planet, score),
            delta,
            deltaLabel,
            lastTakenAt: latestData.completedAt,
          };
        });

      setPlanetResults(results);
    } finally {
      setIsLoading(false);
    }
  }, [userId, birthProfileId]);

  // Load on mount and when userId changes
  useEffect(() => {
    loadLatestScores();
  }, [loadLatestScores]);

  const saveResponse = useCallback(async (
    roundId: string,
    questionId: string,
    planet: string,
    score: number,
    bpId?: string | null,
  ) => {
    if (!userId) return;
    const { error } = await supabase
      .from('planet_friction_responses')
      .upsert(
        {
          user_id: userId,
          birth_profile_id: bpId ?? birthProfileId ?? null,
          round_id: roundId,
          question_id: questionId,
          planet,
          score,
          taken_at: new Date().toISOString(),
        },
        { onConflict: 'user_id,round_id,question_id' },
      );
    if (error) {
      console.error('[useKarmicFriction] saveResponse error:', error);
    }
  }, [userId, birthProfileId]);

  return {
    frictionScoresByPlanet,
    planetResults,
    latestRoundId,
    latestCompletedAt,
    isLoading,
    saveResponse,
    loadLatestScores,
  };
}
